import { useEffect, useState } from 'react';
import {
  ApiFailure,
  PortalEntryRunStatus,
  type PortalRunDto,
  type PortalRunLatestResponse,
  type PortalRunStartResponse,
} from '@signalvote/shared';
import { mapRead, skip, type CurrentRead } from '@signalvote/data';
import { campaignHeaders, defineQuery, defineWrite, edit } from '../../lib/data';
import { apiJson, type ApiErrorBody } from './apiFetch';

const POLL_WHILE_ACTIVE_MS = 2_000;

export const isPortalRunActive = (run: PortalRunDto | null | undefined): boolean => {
  const finished: readonly PortalEntryRunStatus[] = [PortalEntryRunStatus.Completed, PortalEntryRunStatus.Failed, PortalEntryRunStatus.Cancelled];
  return !!run && !finished.includes(run.status);
};

const apiError = (fallback: string) => (body: ApiErrorBody | null, response: Response) =>
  new ApiFailure(response.status, typeof body?.code === 'string' ? body.code : null, body?.error || fallback, null, body ?? {});

interface RunParams {
  reportId: string;
  noticeOf: string | null;
  /** Not part of the key: whether the last answer was a run still going. */
  poll?: boolean;
}

/**
 * The portal-entry worker moves a run along with no write from this page, so its write set never
 * reaches the panel: the read polls while the run is still going.
 */
const portalRun = defineQuery({
  key: ({ reportId, noticeOf }: RunParams) => ['finance', 'reports', 'portal-run', reportId, noticeOf],
  reads: 'GET /api/finance/reports/{reportId}/portal-run',
  fetch: ({ reportId, noticeOf }, { signal, campaignId }) =>
    apiJson<PortalRunLatestResponse>(
      `/api/finance/reports/${reportId}/portal-run${noticeOf ? `?noticeOf=${encodeURIComponent(noticeOf)}` : ''}`,
      { signal, headers: campaignHeaders(campaignId), error: 'Failed to load the portal run' },
    ),
  refetchInterval: ({ poll }) => (poll ? POLL_WHILE_ACTIVE_MS : false),
});

/**
 * The report's latest portal-entry run, or with `noticeOf` its latest run of that row's 72/24-hour
 * notice, polled while it is still going. Opening it clears the report's badge.
 */
export function usePortalRun(reportId: string, enabled: boolean, noticeOf: string | null = null): CurrentRead<PortalRunDto | null> {
  const [poll, setPoll] = useState(false);
  const read = mapRead(portalRun(enabled ? { reportId, noticeOf, poll } : skip), (response) => response.run);
  const active = read.status === 'ready' && isPortalRunActive(read.data);
  useEffect(() => setPoll(active), [active]);
  return read as CurrentRead<PortalRunDto | null>;
}

function showRun(run: PortalRunDto) {
  return [edit(portalRun, { reportId: run.reportId, noticeOf: run.noticeOf }, () => ({ run }))];
}

interface StartPortalRun {
  reportId: string;
  concurrencyVersion: number;
  /** Runs the notice this row owes instead of the whole report. */
  noticeOf?: string | null;
}

/** Starts a run and shows it at once; the report detail picks up its bumped token from the write set. */
const useStartPortalRunWrite = defineWrite({
  send: ({ reportId, concurrencyVersion, noticeOf }: StartPortalRun) =>
    apiJson<PortalRunStartResponse>(`/api/finance/reports/${reportId}/portal-runs`, {
      method: 'POST',
      json: noticeOf ? { concurrencyVersion, noticeOf } : { concurrencyVersion },
      error: apiError('Failed to start the portal run'),
    }),
  land: ({ run }) => showRun(run),
});

export function useStartPortalRun() {
  const { mutate, isPending, failure } = useStartPortalRunWrite();
  return { startRun: mutate, isStarting: isPending, startError: failure };
}

/** Cancels a run with its concurrency token; the cancelled run, with its new token, replaces the shown one. */
const useCancelPortalRunWrite = defineWrite({
  send: (run: PortalRunDto) =>
    apiJson<PortalRunDto>(`/api/finance/portal-runs/${run.id}/cancel`, {
      method: 'POST',
      json: { concurrencyVersion: run.concurrencyVersion },
      error: apiError('Failed to cancel the portal run'),
    }),
  land: (run) => showRun(run),
});

export function useCancelPortalRun() {
  const { mutate, isPending, failure } = useCancelPortalRunWrite();
  return { cancelRun: mutate, isCancelling: isPending, cancelError: failure };
}
